import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Container, Row, Col, Button, Input, Table, Badge } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import SasbossCompanyPicker from './SasbossCompanyPicker'
import {
  fetchSasbossEnterprises,
  unmapSasbossEnterprise,
  triggerSasbossSync,
  exportSasbossDidNumbers,
} from '../../actions/sasbossDidActions'

const formatDate = (value) => {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const SasbossDid = () => {
  const dispatch = useDispatch()
  const { loading, enterprises, syncTriggering } = useSelector((state) => state.sasbossDid)
  const [filter, setFilter] = useState('')
  // enterpriseIds whose "remap" picker is open (mapped rows are read-only until toggled).
  const [remapping, setRemapping] = useState({})

  useEffect(() => {
    dispatch(fetchSasbossEnterprises())
  }, [dispatch])

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return enterprises
    return (enterprises || []).filter((e) =>
      [e.enterpriseName, e.enterpriseId, e.abn, e.mappedCwCompanyName]
        .filter(Boolean)
        .some((v) => v.toString().toLowerCase().includes(q)),
    )
  }, [enterprises, filter])

  const mappedCount = useMemo(
    () => (enterprises || []).filter((e) => e.mappedCwCompanyId).length,
    [enterprises],
  )

  const toggleRemap = (id, open) =>
    setRemapping((prev) => ({ ...prev, [id]: open }))

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-3">
          <Col>
            <PageHeading>SASBOSS DID Numbers</PageHeading>
            <small className="text-muted">
              Map each SASBOSS enterprise to a ConnectWise company; billable DID numbers sync to the
              company's Cloud Voice Billing configuration item.
            </small>
          </Col>
          <Col xs="auto">
            <Button
              color="primary"
              onClick={() => dispatch(triggerSasbossSync())}
              disabled={syncTriggering}
            >
              {syncTriggering ? (
                <span className="spinner-border spinner-border-sm mr-2" />
              ) : null}
              Sync now
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md="6">
            <Input
              type="text"
              placeholder="Filter by enterprise, ABN, or company..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              bsSize="sm"
            />
          </Col>
          <Col className="d-flex align-items-center justify-content-end">
            <small className="text-muted">
              {mappedCount} of {(enterprises || []).length} enterprises mapped
            </small>
          </Col>
        </Row>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Enterprise</th>
                <th>ABN</th>
                <th>Status</th>
                <th>ConnectWise company</th>
                <th className="text-right">Billable DIDs</th>
                <th>Last synced</th>
                <th>Actions</th>
                <th style={{ minWidth: '280px' }}>Mapping</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-3">
                    No enterprises.
                  </td>
                </tr>
              ) : (
                filtered.map((e) => {
                  const isMapped = !!e.mappedCwCompanyId
                  const showPicker = !isMapped || remapping[e.enterpriseId]
                  return (
                    <tr key={e.enterpriseId}>
                      <td>
                        <strong>{e.enterpriseName}</strong>
                        <div className="text-muted small">#{e.enterpriseId}</div>
                      </td>
                      <td className="small">{e.abn || '—'}</td>
                      <td>
                        <Badge color={e.enterpriseStatus === 'active' ? 'success' : 'secondary'}>
                          {e.enterpriseStatus || 'unknown'}
                        </Badge>
                      </td>
                      <td className="small">
                        {isMapped ? (
                          <>
                            {e.mappedCwCompanyName}
                            <span className="text-muted ml-1">#{e.mappedCwCompanyId}</span>
                          </>
                        ) : (
                          <span className="text-muted">Unmapped</span>
                        )}
                      </td>
                      <td className="text-right">{isMapped ? (e.lastBillableCount ?? '—') : '—'}</td>
                      <td className="small">{isMapped ? formatDate(e.lastSyncedAt) : '—'}</td>
                      <td>
                        <Button
                          size="sm"
                          color="outline-secondary"
                          title="Download every number this enterprise holds, billable or not"
                          onClick={() =>
                            dispatch(exportSasbossDidNumbers(e.enterpriseId, e.enterpriseName))
                          }
                        >
                          Export CSV
                        </Button>
                      </td>
                      <td>
                        {showPicker ? (
                          <SasbossCompanyPicker
                            enterpriseId={e.enterpriseId}
                            enterpriseName={e.enterpriseName}
                            submitLabel={isMapped ? 'Remap' : 'Map'}
                            onMapped={() => toggleRemap(e.enterpriseId, false)}
                            onCancel={isMapped ? () => toggleRemap(e.enterpriseId, false) : undefined}
                          />
                        ) : (
                          <div className="d-flex" style={{ gap: '0.5rem' }}>
                            <Button
                              size="sm"
                              color="outline-secondary"
                              onClick={() => toggleRemap(e.enterpriseId, true)}
                            >
                              Remap
                            </Button>
                            <Button
                              size="sm"
                              color="outline-danger"
                              onClick={() => dispatch(unmapSasbossEnterprise(e.enterpriseId))}
                            >
                              Unmap
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        )}
      </Container>
    </PageContainer>
  )
}

export default SasbossDid
